import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import axiosService from '../../inits/axios';
import PageLoader from '../../components/Common/PageLoader';
import CreateFaq from '../../components/Faqs/CreateFaq';
import { toast } from 'react-toastify';

const CreateFaqsContainer = () => {
  // Router hooks
  const params = useParams();
  const navigate = useNavigate();

  // Redux state
  const categories = useSelector((state) => state.auth.categories);

  // Local state
  const [mode, setMode] = useState('CREATE');
  const [faqToBeEdit, setFaqToBeEdit] = useState(null);
  const [accessMode, setAccessMode] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  // Set access based on categories
  const setAccess = useCallback(() => {
    try {
      const faqCategory = categories.find((category) => category.link === 'faqs');
      if (faqCategory) {
        setAccessMode(faqCategory.mode);
        setAccessDenied(false);
      } else {
        throw new Error('FAQ category not found');
      }
    } catch (err) {
      setAccessDenied(true);
    }
  }, [categories]);

  // Fetch FAQ for edit mode
  const getFaq = useCallback(async (faqId) => {
    try {
      const response = await axiosService.get(
        `/faqs/${faqId}`,
        {},
        { 'Content-Type': 'application/json' }
      );
      setFaqToBeEdit(response);
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      navigate('/faqs');
    }
  }, [navigate]);

  // Handle edit FAQ
  const onEdit = useCallback(async (faq) => {
    try {
      const response = await axiosService.put(`/faqs/${faq.id}`, faq);
      setFaqToBeEdit(response);
      toast.success('Successfully updated');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      // Check if it's auth error
      if (error.status === 401 || error.message?.includes('authorization')) {
        toast.error('Authorization error - Please login again');
      }
      navigate('/faqs');
    }
  }, [navigate]);

  // Handle create FAQ
  const onCreate = useCallback(async (faqInfo) => {
    try {
      await axiosService.post('/faqs', faqInfo);
      toast.success('FAQ created successfully');
      navigate('/faqs');
    } catch (error) {
      console.error('Error creating FAQ:', error);
      // Check if it's auth error
      if (error.status === 401 || error.message?.includes('authorization')) {
        toast.error('Authorization error - Please login again');
      }
      navigate('/faqs');
    }
  }, [navigate]);

  // Initialize component on mount
  useEffect(() => {
    // Set mode based on params
    setMode(params.mode === 'edit' ? 'EDIT' : 'CREATE');

    // Set access permissions
    setAccess();

    // Fetch FAQ if ID is present
    if (params.id) {
      getFaq(params.id);
    }
  }, [params.id, params.mode, getFaq, setAccess]);

  // Update access when categories change
  useEffect(() => {
    setAccess();
  }, [categories, setAccess]);

  // Loading state for edit mode
  if (mode === 'EDIT' && !faqToBeEdit) {
    return <PageLoader />;
  }

  // Access denied state
  if (accessDenied) {
    return <div>This category is not accessible to the logged in user.</div>;
  }

  return (
    <section>
      <CreateFaq
        accessMode={accessMode}
        onCreate={onCreate}
        onEdit={onEdit}
        initialValues={faqToBeEdit}
        mode={mode}
      />
    </section>
  );
};

export default CreateFaqsContainer;
